/**
 * User Profile Page Component
 * Coláiste Lurgan Management System - User profile management and customization
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { useToast } from '../../hooks/use-toast';
import {
  User,
  Mail,
  Shield,
  Phone,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Save,
  Settings,
  Info,
  Sun,
  Moon,
  Monitor,
  SendHorizonal,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';

// Humanise role enum → display label per language
const ROLE_LABELS = {
  ADMIN:       { en: 'Administrator', ga: 'Riarthóir' },
  TEACHER:     { en: 'Teacher',       ga: 'Múinteoir' },
  INTERVIEWER: { en: 'Interviewer',   ga: 'Agallóir'  },
  HOUSE_OWNER: { en: 'House Owner',   ga: 'Úinéir Tí' },
  PARENT:      { en: 'Parent',        ga: 'Tuismitheoir' },
  OFFICE:      { en: 'Office',        ga: 'Oifig'     },
};

// Password strength scorer
function getPasswordStrength(password) {
  if (!password) return null;
  let score = 0;
  if (password.length >= 8)  score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 1) return 'weak';
  if (score <= 2) return 'fair';
  if (score <= 3) return 'good';
  return 'strong';
}

const STRENGTH_CONFIG = {
  weak:   { bars: 1, color: 'bg-red-500',    key: 'profile.passwordStrength.weak'   },
  fair:   { bars: 2, color: 'bg-yellow-500', key: 'profile.passwordStrength.fair'   },
  good:   { bars: 3, color: 'bg-blue-500',   key: 'profile.passwordStrength.good'   },
  strong: { bars: 4, color: 'bg-green-500',  key: 'profile.passwordStrength.strong' },
};

function PasswordStrengthMeter({ password, t }) {
  const strength = getPasswordStrength(password);
  if (!strength) return null;
  const config = STRENGTH_CONFIG[strength];
  return (
    <div className="space-y-1 mt-1">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors duration-300',
              i <= config.bars ? config.color : 'bg-muted'
            )}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{t(config.key)}</p>
    </div>
  );
}

export function Profile() {
  const { user, refetchUser, logout } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const isParent = user?.user_type === 'PARENT';

  // Profile form state
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone_number: user?.phone_number || '',
    email: user?.email || '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirm: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);


  // Detect unsaved changes
  const emailChanged = profileData.email !== (user?.email || '');
  const isDirty = useMemo(() => (
    profileData.first_name   !== (user?.first_name   || '') ||
    profileData.last_name    !== (user?.last_name    || '') ||
    profileData.phone_number !== (user?.phone_number || '') ||
    profileData.email        !== (user?.email        || '')
  ), [profileData, user]);

  const handleProfileUpdate = async () => {
    setIsLoading(true);
    try {
      await api.auth.patchProfile({
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        phone_number: profileData.phone_number,
      });
      if (emailChanged) {
        await api.auth.changeEmail(profileData.email);
      }
      await refetchUser();
      toast({
        title: t('profile.toast.profileUpdated'),
        description: emailChanged
          ? t('profile.emailChangeSent', { email: profileData.email })
          : t('profile.toast.profileUpdatedDesc'),
        variant: 'success',
      });
      setIsEditing(false);
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.non_field_errors?.[0] ||
        error.message ||
        t('profile.toast.updateFailedDesc');
      toast({
        title: t('profile.toast.updateFailed'),
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setProfileData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone_number: user?.phone_number || '',
      email: user?.email || '',
    });
    setIsEditing(false);
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.new_password_confirm) {
      toast({
        title: t('profile.toast.passwordMismatch'),
        description: t('profile.toast.passwordMismatchDesc'),
        variant: 'destructive',
      });
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast({
        title: t('profile.toast.passwordTooShort'),
        description: t('profile.toast.passwordTooShortDesc'),
        variant: 'destructive',
      });
      return;
    }
    setIsLoading(true);
    try {
      await api.auth.changePassword(user.id, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        new_password_confirm: passwordData.new_password_confirm,
      });
      toast({
        title: t('profile.toast.passwordUpdated'),
        description: t('profile.toast.passwordUpdatedDesc'),
        variant: 'success',
      });
      setPasswordData({ current_password: '', new_password: '', new_password_confirm: '' });
      setIsPasswordOpen(false);
      // Backend invalidates the token — log out so user re-authenticates
      setTimeout(() => logout(), 1500);
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.non_field_errors?.[0] ||
        t('profile.toast.passwordChangeFailedDesc');
      toast({
        title: t('profile.toast.passwordChangeFailed'),
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Avatar: initials from name, fallback to username
  const initials = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .map((n) => n[0].toUpperCase())
    .join('') || (user?.username?.[0] || '?').toUpperCase();

  // Humanised role label
  const roleLabel = user?.user_type
    ? (ROLE_LABELS[user.user_type]?.[i18n.language] ?? ROLE_LABELS[user.user_type]?.en ?? user.user_type)
    : null;

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username;

  return (
    <div className="space-y-6">

      {/* ── Hero Header ───────────────────────────────────────── */}
      <Card>
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center gap-6">
            {/* Avatar — larger than before */}
            <div
              className="h-20 w-20 rounded-full flex items-center justify-center text-white text-2xl font-bold shrink-0 select-none shadow-sm"
              style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482)' }}
            >
              {initials}
            </div>

            {/* Identity */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold tracking-tight">{fullName}</h1>
                {roleLabel && (
                  <Badge variant="secondary" className="shrink-0">{roleLabel}</Badge>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
                <Mail className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{user?.email}</span>
                {user?.is_email_verified ? (
                  <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                ) : (
                  <AlertCircle className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
                )}
              </div>
              {user?.username && (
                <p className="text-xs text-muted-foreground mt-0.5 font-mono">@{user.username}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Main Grid ─────────────────────────────────────────── */}
      <div className="grid gap-6 lg:grid-cols-3">

        {/* Personal Information — 2 cols */}
        <Card className={cn('lg:col-span-2 transition-all duration-200', isEditing && 'ring-2 ring-primary/30')}>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {t('profile.personal.title')}
                </CardTitle>
                <CardDescription>{t('profile.personal.description')}</CardDescription>
              </div>
              {isEditing && isDirty && (
                <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs shrink-0 mt-0.5">
                  {t('profile.unsavedChanges')}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Username + Email — same row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="username">{t('profile.username')}</Label>
                <div className="flex items-center gap-2">
                  <Input id="username" value={user?.username || ''} disabled className="font-mono flex-1" />
                  <Shield className="h-4 w-4 text-muted-foreground shrink-0" title={t('profile.usernameNote')} />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">{t('profile.email')}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!isEditing}
                    className={cn('flex-1 min-w-0', isEditing && 'ring-1 ring-primary/40')}
                  />
                  {!isEditing && (user?.is_email_verified ? (
                    <Badge variant="outline" className="text-green-600 shrink-0">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {t('profile.verified')}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-yellow-600 shrink-0">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {t('profile.unverified')}
                    </Badge>
                  ))}
                </div>
                {isEditing && emailChanged && (
                  <p className="text-xs text-muted-foreground">{t('profile.emailChangeWarning')}</p>
                )}
              </div>
            </div>

            {/* First / Last name / Phone */}
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="first_name">{t('profile.firstName')}</Label>
                <Input
                  id="first_name"
                  value={profileData.first_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, first_name: e.target.value }))}
                  disabled={!isEditing}
                  className={cn(isEditing && 'ring-1 ring-primary/40')}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="last_name">{t('profile.lastName')}</Label>
                <Input
                  id="last_name"
                  value={profileData.last_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, last_name: e.target.value }))}
                  disabled={!isEditing}
                  className={cn(isEditing && 'ring-1 ring-primary/40')}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone_number" className="flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {t('profile.phoneNumber')}
                  {isParent && <span className="text-destructive text-xs ml-1">*</span>}
                </Label>
                <Input
                  id="phone_number"
                  type="tel"
                  value={profileData.phone_number}
                  onChange={(e) => setProfileData(prev => ({ ...prev, phone_number: e.target.value }))}
                  disabled={!isEditing}
                  placeholder={isParent ? t('profile.phoneRequired') : ''}
                  className={cn(isEditing && 'ring-1 ring-primary/40')}
                />
              </div>
            </div>

            {/* Action buttons — right aligned */}
            <div className="flex justify-end gap-2">
              {!isEditing ? (
                <Button onClick={() => setIsEditing(true)}>
                  <Settings className="h-4 w-4 mr-2" />
                  {t('profile.editProfile')}
                </Button>
              ) : (
                <>
                  <Button onClick={handleProfileUpdate} disabled={isLoading || !isDirty}>
                    {emailChanged
                      ? <SendHorizonal className="h-4 w-4 mr-2" />
                      : <Save className="h-4 w-4 mr-2" />
                    }
                    {isLoading ? t('common.saving') : emailChanged ? t('profile.saveAndSend') : t('common.save')}
                  </Button>
                  <Button variant="outline" onClick={handleCancelEdit}>
                    {t('common.cancel')}
                  </Button>
                </>
              )}
            </div>

            {/* Parent sync note */}
            {isParent && isEditing && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-muted text-sm text-muted-foreground">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{t('profile.parentSyncNote')}</span>
              </div>
            )}

            <Separator />

            {/* Password */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t('profile.password')}</p>
                  <p className="text-xs text-muted-foreground">{t('profile.passwordNote')}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsPasswordOpen(v => !v);
                    setPasswordData({ current_password: '', new_password: '', new_password_confirm: '' });
                  }}
                >
                  {isPasswordOpen ? t('common.cancel') : t('profile.changePassword')}
                </Button>
              </div>

              {isPasswordOpen && (
                <form
                  onSubmit={(e) => { e.preventDefault(); handlePasswordChange(); }}
                  className="space-y-3 pt-3 border-t"
                >
                  {[
                    { key: 'current', field: 'current_password', name: 'current-password', autocomplete: 'current-password' },
                    { key: 'new',     field: 'new_password',     name: 'new-password',     autocomplete: 'new-password'     },
                    { key: 'confirm', field: 'new_password_confirm', name: 'new-password-confirm', autocomplete: 'new-password' },
                  ].map(({ key, field, name, autocomplete }) => (
                    <div key={key} className="grid gap-1.5">
                      <Label htmlFor={field} className="text-xs">{t(`profile.${key}Password`)}</Label>
                      <div className="relative">
                        <Input
                          id={field}
                          name={name}
                          type={showPasswords[key] ? 'text' : 'password'}
                          autoComplete={autocomplete}
                          value={passwordData[field]}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, [field]: e.target.value }))}
                          className="h-8 text-sm pr-8"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-2 py-1"
                          onClick={() => setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }))}
                        >
                          {showPasswords[key] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                      {key === 'new' && (
                        <PasswordStrengthMeter password={passwordData.new_password} t={t} />
                      )}
                    </div>
                  ))}
                  <Button type="submit" size="sm" disabled={isLoading} className="w-full mt-1">
                    {isLoading ? t('common.saving') : t('profile.updatePassword')}
                  </Button>
                </form>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preferences — 1 col */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {t('profile.preferences')}
            </CardTitle>
            <CardDescription>{t('profile.preferencesNote')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">

            {/* Language */}
            <div>
              <p className="text-sm font-medium mb-2">{t('profile.language')}</p>
              <div className="flex p-1 rounded-lg bg-muted gap-0.5">
                {[
                  { code: 'en', label: 'English', icon: '🇬🇧' },
                  { code: 'ga', label: 'Gaeilge', icon: '🇮🇪' },
                ].map(({ code, label, icon }) => (
                  <button
                    key={code}
                    onClick={() => i18n.changeLanguage(code)}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                      i18n.language === code
                        ? 'bg-background shadow-sm text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <span>{icon}</span>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Appearance */}
            <div>
              <p className="text-sm font-medium mb-2">{t('profile.appearance')}</p>
              <div className="flex p-1 rounded-lg bg-muted gap-0.5">
                {[
                  { value: 'light',  Icon: Sun,     label: t('profile.theme.light')  },
                  { value: 'dark',   Icon: Moon,    label: t('profile.theme.dark')   },
                  { value: 'system', Icon: Monitor, label: t('profile.theme.system') },
                ].map(({ value, Icon, label }) => (
                  <button
                    key={value}
                    onClick={() => setTheme(value)}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all',
                      theme === value
                        ? 'bg-background shadow-sm text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

          </CardContent>
        </Card>

      </div>
    </div>
  );
}

export default Profile;
